/* eslint-disable react/hooks -- this file IS the hook-injection seam; see the note below. */

/*
 * `useTenantParams` and `useUrlState` are passed to CrudConfigProvider as function
 * references so `@restavo/crud` need not depend on this app's router or tenant context.
 * The rule cannot see that the injected identities are stable for the lifetime of the
 * provider, so it reads each one as a dynamically selected Hook.
 *
 * Scoped to this file on purpose. It replaces a blanket `react/hooks: off` across all of
 * `packages/crud/**` and `apps/portal/src/crud/**`, which also silenced any NEW violation
 * in those directories — including real ones. The genuine fix is to invert the seam so the
 * portal injects the VALUES these hooks return rather than the hooks themselves; that is a
 * deliberate refactor, not a lint tweak.
 */
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CrudConfigProvider } from '@restavo/crud';
import { useTenantContext } from 'src/context/useTenantContext';
import { useMoney } from 'src/hooks/useMoney';
import { portalDateBound } from './dateBound';
import { resolveCrudError } from './resolveCrudError';
import { useRouterUrlState } from './useRouterUrlState';

/** Injected, not called here: this provider mounts above the tenant context useMoney needs. */
const useFormatCurrency = () => useMoney().format;

/**
 * The single seam between @restavo/crud and this app. useTenantParams is passed as a
 * function reference and only invoked while a CRUD page renders — which is always inside
 * TenantContextProvider — so this can safely mount above the router's context loader.
 */
export const CrudProviders = ({ children }: { children: React.ReactNode }) => {
  const { t, i18n } = useTranslation();
  return (
    <CrudConfigProvider
      useTenantParams={useTenantContext}
      translate={t}
      resolveErrorMessage={resolveCrudError}
      useUrlState={useRouterUrlState}
      useFormatCurrency={useFormatCurrency}
      formatDate={(value, opts) =>
        value == null ? '' : opts?.relative ? dayjs(value).fromNow() : dayjs(value).format('DD/MM/YYYY')
      }
      dateBound={portalDateBound}
      locale={i18n.language}
    >
      {children}
    </CrudConfigProvider>
  );
};
